import {
  ActionIcon,
  Box,
  Center,
  Checkbox,
  Group,
  LoadingOverlay,
  Pagination,
  Paper,
  Portal,
  Select,
  Table,
  Text,
} from "@mantine/core";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "../SvgIconRaw";
import NumberRangeFilter from "./NumberRangeFilter";
import Toolbar from "./Toolbar";
import type { NumberRangeFilterValue } from "./filterFns";
import type {
  MeqColumnDef,
  MeqDensity,
  MeqRowData,
  MeqTableProps,
} from "./types";
import {
  getColumnFilterVariant,
  renderCellContent,
  renderHeaderContent,
  useMeqTableInstance,
} from "./useMeqTableInstance";

/**
 * Core MeqTable rendering (INS-1606): sticky header + detail-panel
 * expansion + row selection + row actions forced last + fullscreen, plus
 * client-side sorting/pagination, loading/empty states, and the full
 * toolbar (title/custom-actions slot, global filter, show/hide-columns
 * menu, density toggle, fullscreen toggle, CSV export — see Toolbar.tsx),
 * and a number-range column-filter row (only rendered when
 * `hasColumnFilters` is on — column filters stay off by default, matching
 * the old wrapper).
 *
 * Every user-facing string (row-selection/expand-row aria-labels,
 * empty-state text) defaults from i18next `table.*` and is overridable via
 * the `localization` prop (INS-1606 i18n-localization task).
 */

const FULLSCREEN_Z_INDEX = 200; // Mantine's modal z-index
const FULLSCREEN_SCROLL_MAX_HEIGHT = "calc(100vh - 150px)";

/** MRT's default rowsPerPageOptions — parity with the old bottom toolbar. */
const PAGE_SIZE_OPTIONS = ["5", "10", "15", "20", "25", "30", "50", "100"];

/**
 * Old-wrapper parity: default density is "compact" (initialState.density),
 * and the values are MRT's exact per-density cell paddings (0.5/1/1.5rem) —
 * Mantine's nearest spacing tokens (xs = 10px vs MRT's 8px) render rows
 * visibly taller.
 */
const DENSITY_VERTICAL_SPACING: Record<MeqDensity, string> = {
  compact: "0.5rem",
  comfortable: "1rem",
  spacious: "1.5rem",
};

// Old-wrapper header text colour. MRT used `mytext.tertiary`, which was
// gray-600 in light but grayDarkMode-300 (#CECFD2) in dark. No single current
// text token reproduces that combo — `text-tertiary` matches only the light
// value (its dark value is the more-muted grayDarkMode-400, which washed the
// dark headers out) and `text-secondary` matches only the dark value — so
// compose both with light-dark() to restore the exact old header colour
// (light-mode value unchanged).
const HEADER_TEXT_COLOR =
  "light-dark(var(--mantine-color-text-tertiary), var(--mantine-color-text-secondary))";

/**
 * Old-wrapper parity header treatment (muiTableHeadCellProps: semibold,
 * mytext.tertiary on mybg.secondary) — Mantine's Th defaults (700 weight,
 * full text color, no band) read far heavier than the MRT tables did.
 */
const HEADER_TH_PROPS = {
  fw: 600,
  c: HEADER_TEXT_COLOR,
  bg: "var(--mantine-color-bg-secondary)",
} as const;

const MeqTable = <TData extends MeqRowData>(props: MeqTableProps<TData>) => {
  const {
    title,
    isLoading = false,
    noRowsFallback,
    maxHeight = 480,
    fillHeight = false,
    hasBorder = true,
    hasCardSurface = false,
    hasStripedRows = true,
    getRowColor,
    getHeaderCellStyle,
    getBodyCellStyle,
    pinnedColumnIds,
    onRowClick,
    hasToolbar = true,
    hasStickyHeader = true,
    hasPagination = true,
    hasFullscreenToggle = true,
    hasBorderedControls = false,
    hasGlobalFilter = true,
    hasColumnFilters = false,
    hasColumnHiding = true,
    hasDensityToggle = false,
    hasRowSelection = false,
    hasSelectAll = true,
    hasMultiRowSelection = true,
    hasExport = false,
    isExportButtonIcon = true,
    onExportDataToCsv,
    exportFileName,
    renderDetailPanel,
    renderRowActions,
    renderToolbarActions,
    renderToolbarActionsRight,
    renderToolbarActionsRightEnd,
    localization,
  } = props;
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [internalDensity, setInternalDensity] = useState<MeqDensity>(
    props.initialState?.density ?? "compact",
  );
  const { table, meqInstance, toggleRowExpanded } = useMeqTableInstance(props);
  const density: MeqDensity = props.state?.density ?? internalDensity;

  // Widths for columns whose def EXPLICITLY sets `size` (keyed by id ??
  // accessorKey). Columns are content-sized by default (nowrap cells);
  // an explicit size is the per-column dial for "give this one more air".
  // Read from the raw defs — the engine's merged columnDef.size always
  // reports TanStack's 150 default, so it can't distinguish explicit sizes.
  const { columns } = props;
  const explicitColumnWidths = useMemo(() => {
    const widths: Record<string, number> = {};
    const collect = (defs: MeqColumnDef<TData>[]) => {
      defs.forEach((def) => {
        if (def.columns?.length) {
          collect(def.columns);
        } else if (def.size !== undefined) {
          // Mirror TanStack's own id resolution: id ?? accessorKey ?? header.
          const columnId = def.id ?? def.accessorKey ?? def.header;
          if (columnId) {
            widths[columnId] = def.size;
          }
        }
      });
    };
    collect(columns);
    return widths;
  }, [columns]);

  // Left-pinned (sticky) columns — old wrapper parity with
  // `initialState.columnPinning.left`. Offsets are MEASURED from the
  // rendered leaf header cells (columns are content-sized by default, so
  // declared sizes can't be trusted for pixel offsets); a ResizeObserver on
  // the table re-measures when auto layout redistributes widths.
  const pinnedColumnSet = useMemo(
    () => new Set(pinnedColumnIds ?? []),
    [pinnedColumnIds],
  );
  const hasPinnedColumns = pinnedColumnSet.size > 0;
  const [pinnedColumnWidths, setPinnedColumnWidths] = useState<
    Record<string, number>
  >({});
  const pinnedHeaderCells = useRef(new Map<string, HTMLTableCellElement>());
  const tableElementRef = useRef<HTMLTableElement>(null);
  const measurePinnedColumns = useCallback(() => {
    if (!hasPinnedColumns) {
      return;
    }
    const next: Record<string, number> = {};
    pinnedHeaderCells.current.forEach((element, columnId) => {
      next[columnId] = element.getBoundingClientRect().width;
    });
    setPinnedColumnWidths((prev) => {
      const prevKeys = Object.keys(prev);
      const isUnchanged =
        prevKeys.length === Object.keys(next).length &&
        prevKeys.every((key) => prev[key] === next[key]);
      return isUnchanged ? prev : next;
    });
  }, [hasPinnedColumns]);
  // After every render: pinned cells may have re-rendered with new content
  // (guarded no-op setState when widths are unchanged).
  useLayoutEffect(() => {
    measurePinnedColumns();
  });
  useEffect(() => {
    if (!hasPinnedColumns || !tableElementRef.current) {
      return;
    }
    const observer = new ResizeObserver(measurePinnedColumns);
    observer.observe(tableElementRef.current);
    return () => observer.disconnect();
  }, [hasPinnedColumns, measurePinnedColumns]);

  // Utility columns (selection/expand) sit before the data columns and are
  // pinned too whenever pinning is active (MRT pinned its display columns
  // automatically), so data-column offsets start after them.
  const utilityColumnsWidth =
    (hasRowSelection ? 40 : 0) + (renderDetailPanel ? 40 : 0);
  const pinnedColumnOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    let left = hasPinnedColumns ? utilityColumnsWidth : 0;
    (pinnedColumnIds ?? []).forEach((columnId) => {
      offsets[columnId] = left;
      left += pinnedColumnWidths[columnId] ?? 0;
    });
    return offsets;
  }, [
    hasPinnedColumns,
    pinnedColumnIds,
    pinnedColumnWidths,
    utilityColumnsWidth,
  ]);
  const registerPinnedHeaderCell = useCallback(
    (columnId: string) => (element: HTMLTableCellElement | null) => {
      if (element) {
        pinnedHeaderCells.current.set(columnId, element);
      } else {
        pinnedHeaderCells.current.delete(columnId);
      }
    },
    [],
  );
  const pinnedThStyle = (left: number | undefined) =>
    left === undefined
      ? undefined
      : ({ position: "sticky", left, zIndex: 2 } as const);

  const noRecordsText =
    localization?.noRecordsToDisplay ?? t("table.noRecordsToDisplay");

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const totalColumnCount =
    table.getVisibleLeafColumns().length +
    (hasRowSelection ? 1 : 0) +
    (renderDetailPanel ? 1 : 0) +
    (renderRowActions ? 1 : 0);
  const leafHeaders = headerGroups[headerGroups.length - 1]?.headers ?? [];

  const toolbar = (
    <Toolbar
      title={title}
      meqInstance={meqInstance}
      renderToolbarActions={renderToolbarActions}
      renderToolbarActionsRight={renderToolbarActionsRight}
      renderToolbarActionsRightEnd={renderToolbarActionsRightEnd}
      hasGlobalFilter={hasGlobalFilter}
      hasColumnHiding={hasColumnHiding}
      hasDensityToggle={hasDensityToggle}
      hasFullscreenToggle={hasFullscreenToggle}
      hasBorderedControls={hasBorderedControls}
      hasExport={hasExport}
      isExportButtonIcon={isExportButtonIcon}
      onExportDataToCsv={onExportDataToCsv}
      exportFileName={exportFileName}
      density={density}
      onDensityChange={setInternalDensity}
      isFullscreen={isFullscreen}
      onToggleFullscreen={() => setIsFullscreen((prev) => !prev)}
      localization={localization}
    />
  );

  const tableCard = (
    <Paper
      withBorder={hasBorder}
      radius="md"
      pos="relative"
      bg={hasCardSurface ? "var(--mantine-color-bg-primary)" : undefined}
      style={
        isFullscreen
          ? { display: "flex", flexDirection: "column", height: "100%" }
          : fillHeight
            ? {
                // maxHeight (not height) so a table with few rows sizes to its
                // content instead of stretching the card to the viewport, while
                // a long table still caps here and scrolls internally.
                display: "flex",
                flexDirection: "column",
                maxHeight: "100%",
                minHeight: 0,
              }
            : undefined
      }
    >
      <LoadingOverlay visible={isLoading} />
      {hasToolbar && toolbar}
      <Table.ScrollContainer
        minWidth={320}
        maxHeight={
          isFullscreen
            ? FULLSCREEN_SCROLL_MAX_HEIGHT
            : fillHeight
              ? "100%"
              : maxHeight
        }
        // fillHeight: shrink-only (no flex-grow) so the scroll area sizes to
        // its rows and only shrinks + scrolls once the card hits its maxHeight;
        // this avoids stretching a short table to fill the viewport.
        style={fillHeight && !isFullscreen ? { minHeight: 0 } : undefined}
      >
        <Table
          ref={tableElementRef}
          stickyHeader={hasStickyHeader}
          highlightOnHover={false}
          // Old-wrapper parity: muiTableHeadCellProps/muiTableBodyCellProps
          // both set borderWidth: 0 — rows separate by striping, not rules.
          withRowBorders={false}
          verticalSpacing={DENSITY_VERTICAL_SPACING[density]}
        >
          <Table.Thead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroupIndex === 0 && hasRowSelection && (
                  <Table.Th
                    rowSpan={headerGroups.length}
                    w={40}
                    miw={40}
                    style={hasPinnedColumns ? pinnedThStyle(0) : undefined}
                    {...HEADER_TH_PROPS}
                  >
                    {hasMultiRowSelection && hasSelectAll && (
                      <Checkbox
                        size="xs"
                        aria-label={
                          localization?.selectAllRows ??
                          t("table.selectAllRows")
                        }
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                      />
                    )}
                  </Table.Th>
                )}
                {headerGroupIndex === 0 && renderDetailPanel && (
                  <Table.Th
                    rowSpan={headerGroups.length}
                    w={40}
                    miw={40}
                    style={
                      hasPinnedColumns
                        ? pinnedThStyle(hasRowSelection ? 40 : 0)
                        : undefined
                    }
                    {...HEADER_TH_PROPS}
                  />
                )}
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const explicitWidth = explicitColumnWidths[header.column.id];
                  const headerCellStyle = getHeaderCellStyle?.({
                    headerRowIndex: headerGroupIndex,
                    columnId: header.column.id,
                  });
                  // A header cell is pinned when EVERY leaf under it is
                  // pinned (a group header over the pinned block sticks
                  // along with its leaves).
                  const leafColumns = header.column.getLeafColumns();
                  const isPinnedHeader =
                    hasPinnedColumns &&
                    leafColumns.every((leafColumn) =>
                      pinnedColumnSet.has(leafColumn.id),
                    );
                  const isPinnedLeaf =
                    isPinnedHeader && header.column.columns.length === 0;
                  return (
                    <Table.Th
                      key={header.id}
                      ref={
                        isPinnedLeaf
                          ? registerPinnedHeaderCell(header.column.id)
                          : undefined
                      }
                      colSpan={header.colSpan}
                      w={explicitWidth}
                      miw={explicitWidth}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      style={{
                        // User-directed fix (parity round 2 rework): headers
                        // no longer wrap — nowrap + the browser's auto table
                        // layout gives each column its one-line header width,
                        // and Table.ScrollContainer scrolls horizontally for
                        // whatever doesn't fit (old MRT behavior). Callers
                        // that want a wrapping header can add an explicit
                        // break in their own `header`/`Header` content.
                        whiteSpace: "nowrap",
                        ...(canSort
                          ? { cursor: "pointer", userSelect: "none" }
                          : undefined),
                        // Mantine puts style PROPS (bg/c/fw) after `style`
                        // when merging, so a getHeaderCellStyle override
                        // routes the header band defaults through `style`
                        // instead — the returned properties must win.
                        ...(headerCellStyle && {
                          backgroundColor: "var(--mantine-color-bg-secondary)",
                          color: HEADER_TEXT_COLOR,
                          fontWeight: 600,
                          ...headerCellStyle,
                        }),
                        ...pinnedThStyle(
                          isPinnedHeader
                            ? pinnedColumnOffsets[leafColumns[0]?.id]
                            : undefined,
                        ),
                      }}
                      {...(headerCellStyle ? {} : HEADER_TH_PROPS)}
                    >
                      {canSort ? (
                        <Group gap={4} wrap="nowrap">
                          {renderHeaderContent(header)}
                          <Box
                            component="span"
                            display="inline-flex"
                            flex="0 0 auto"
                          >
                            {!header.column.getIsSorted() && (
                              <SvgIconRaw
                                name="ChevronSelectorVertical"
                                size={12}
                                color="var(--mantine-color-text-disabled)"
                              />
                            )}
                            {header.column.getIsSorted() === "asc" && (
                              <SvgIconRaw name="ArrowNarrowUp" size={14} />
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <SvgIconRaw name="ArrowNarrowDown" size={14} />
                            )}
                          </Box>
                        </Group>
                      ) : headerCellStyle ? (
                        // Bare content so the override's textAlign can center
                        // it (a flex Group ignores the cell's text-align).
                        renderHeaderContent(header)
                      ) : (
                        <Group gap={4} wrap="nowrap">
                          {renderHeaderContent(header)}
                        </Group>
                      )}
                    </Table.Th>
                  );
                })}
                {/* Row actions column is ALWAYS last, absorbs the leftover
                    width (w=100% in auto table layout) so data columns keep
                    their content-driven size, actions hug the right edge. */}
                {headerGroupIndex === 0 && renderRowActions && (
                  <Table.Th
                    rowSpan={headerGroups.length}
                    w="100%"
                    miw={60}
                    {...HEADER_TH_PROPS}
                  />
                )}
              </Table.Tr>
            ))}
            {hasColumnFilters && (
              <Table.Tr>
                {hasRowSelection && <Table.Th {...HEADER_TH_PROPS} />}
                {renderDetailPanel && <Table.Th {...HEADER_TH_PROPS} />}
                {leafHeaders.map((header) => (
                  <Table.Th key={`${header.id}-filter`} {...HEADER_TH_PROPS}>
                    {getColumnFilterVariant(header) === "range" &&
                      header.column.getCanFilter() && (
                        <NumberRangeFilter
                          value={
                            header.column.getFilterValue() as
                              | NumberRangeFilterValue
                              | undefined
                          }
                          onChange={(value) =>
                            header.column.setFilterValue(value)
                          }
                          localization={localization}
                        />
                      )}
                  </Table.Th>
                ))}
                {renderRowActions && <Table.Th {...HEADER_TH_PROPS} />}
              </Table.Tr>
            )}
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={totalColumnCount}>
                  <Center p="md">
                    {noRowsFallback ?? <Text size="sm">{noRecordsText}</Text>}
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row, rowIndex) => {
                // Striped rows alternate the white base (bg-primary) with the
                // gray-50 tint (bg-secondary). The base must be set explicitly
                // — leaving it undefined falls through to the table body
                // (bg-secondary-subtle / gray-25), which is so close to the
                // tint the zebra all but disappears.
                const stripeBackground = hasStripedRows
                  ? rowIndex % 2 === 1
                    ? "var(--mantine-color-bg-secondary)"
                    : "var(--mantine-color-bg-primary)"
                  : undefined;
                const rowBackground = row.getIsSelected()
                  ? "var(--mantine-primary-color-light)"
                  : (getRowColor?.(row) ?? stripeBackground);
                // Pinned cells need their own opaque background (the row's
                // if it has one) or scrolled-under cells show through.
                const pinnedTdStyle = (
                  left: number | undefined,
                  backgroundColor?: string,
                ) =>
                  left === undefined
                    ? undefined
                    : ({
                        position: "sticky",
                        left,
                        zIndex: 1,
                        backgroundColor:
                          backgroundColor ??
                          rowBackground ??
                          "var(--mantine-color-body)",
                      } as const);
                return (
                  <Fragment key={row.id}>
                    <Table.Tr
                      bg={rowBackground}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={onRowClick ? { cursor: "pointer" } : undefined}
                    >
                      {hasRowSelection && (
                        <Table.Td
                          w={40}
                          miw={40}
                          style={
                            hasPinnedColumns ? pinnedTdStyle(0) : undefined
                          }
                        >
                          <Checkbox
                            size="xs"
                            radius={hasMultiRowSelection ? "sm" : "xl"}
                            aria-label={
                              localization?.selectRow ?? t("table.selectRow")
                            }
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            onChange={row.getToggleSelectedHandler()}
                          />
                        </Table.Td>
                      )}
                      {renderDetailPanel && (
                        <Table.Td
                          w={40}
                          miw={40}
                          style={
                            hasPinnedColumns
                              ? pinnedTdStyle(hasRowSelection ? 40 : 0)
                              : undefined
                          }
                        >
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={
                              row.getIsExpanded()
                                ? (localization?.collapseRow ??
                                  t("table.collapseRow"))
                                : (localization?.expandRow ??
                                  t("table.expandRow"))
                            }
                            onClick={() => toggleRowExpanded(row)}
                          >
                            <SvgIconRaw
                              name={
                                row.getIsExpanded()
                                  ? "ChevronUp"
                                  : "ChevronDown"
                              }
                              size={16}
                            />
                          </ActionIcon>
                        </Table.Td>
                      )}
                      {row.getVisibleCells().map((cell) => {
                        const bodyCellStyle = getBodyCellStyle?.(cell);
                        return (
                          <Table.Td
                            key={cell.id}
                            style={{
                              whiteSpace: "nowrap",
                              ...bodyCellStyle,
                              ...(pinnedColumnSet.has(cell.column.id) &&
                                pinnedTdStyle(
                                  pinnedColumnOffsets[cell.column.id],
                                  bodyCellStyle?.backgroundColor as
                                    | string
                                    | undefined,
                                )),
                            }}
                          >
                            {renderCellContent(cell)}
                          </Table.Td>
                        );
                      })}
                      {renderRowActions && (
                        <Table.Td w="100%" miw={60}>
                          <Group justify="flex-start" wrap="nowrap" gap={4}>
                            {renderRowActions({ row })}
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                    {renderDetailPanel && row.getIsExpanded() && (
                      <Table.Tr>
                        <Table.Td
                          colSpan={totalColumnCount}
                          bg="var(--mantine-color-bg-quarterary)"
                          // Thin grey frame around the expanded content instead
                          // of the table's full cell padding (~10px), which read
                          // as a clunky backing panel (INS-1629).
                          p={2}
                          data-testid="meq-table-detail-panel"
                        >
                          {renderDetailPanel({ row })}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {hasPagination && table.getPageCount() > 1 && (
        <Group justify="flex-end" gap="sm" p="sm">
          <Text size="sm" c="var(--mantine-color-text-tertiary)">
            {localization?.rowsPerPage ?? t("table.rowsPerPage")}
          </Text>
          <Select
            size="xs"
            w={76}
            data={PAGE_SIZE_OPTIONS}
            value={String(table.getState().pagination.pageSize)}
            onChange={(value) => value && table.setPageSize(Number(value))}
            allowDeselect={false}
            aria-label={localization?.rowsPerPage ?? t("table.rowsPerPage")}
          />
          <Pagination
            size="sm"
            total={Math.max(table.getPageCount(), 1)}
            value={table.getState().pagination.pageIndex + 1}
            onChange={(page) => table.setPageIndex(page - 1)}
            // Subtle controls (no borders), closer to the old MRT pages-mode
            // MUI Pagination's text-style buttons.
            styles={{ control: { border: 0 } }}
          />
        </Group>
      )}
    </Paper>
  );

  if (isFullscreen) {
    return (
      <Portal>
        <Box
          pos="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          p="md"
          bg="var(--mantine-color-body)"
          style={{ zIndex: FULLSCREEN_Z_INDEX }}
        >
          {tableCard}
        </Box>
      </Portal>
    );
  }

  return tableCard;
};

export default MeqTable;
